/**
 * Secure Authentication Service
 *
 * This service replaces the hardcoded Cognito client secret implementation
 * with secure backend proxy calls to Lambda functions.
 */
class SecureAuthService {
    constructor() {
        // Use environment variable for API Gateway endpoint
        this.baseUrl = process.env.REACT_APP_AUTH_API_URL || '';
        if (!this.baseUrl) {
            console.warn('REACT_APP_AUTH_API_URL environment variable is not set. Authentication proxy will not work.');
            // Set a fallback URL for development
            this.baseUrl = 'https://x2ydr38bz5.execute-api.us-east-1.amazonaws.com/dev';
            console.info('Using fallback authentication API URL:', this.baseUrl);
        }
    }
    /**
     * Sign in user with username and password
     */
    async signIn(username, password) {
        const response = await this.makeRequest('/auth/signin', {
            username,
            password
        });
        if (!response.success) {
            throw new Error(response.error || 'Sign in failed');
        }
        return response.data;
    }
    /**
     * Sign up new user
     */
    async signUp(username, password, email, userAttributes) {
        const response = await this.makeRequest('/auth/signup', {
            username,
            password,
            email,
            userAttributes
        });
        if (!response.success) {
            throw new Error(response.error || 'Sign up failed');
        }
        return response.data;
    }
    /**
     * Confirm sign up with verification code
     */
    async confirmSignUp(username, confirmationCode) {
        const response = await this.makeRequest('/auth/confirm-signup', {
            username,
            confirmationCode
        });
        if (!response.success) {
            throw new Error(response.error || 'Email confirmation failed');
        }
    }
    /**
     * Initiate password reset
     */
    async resetPassword(username) {
        const response = await this.makeRequest('/auth/reset-password', {
            username
        });
        if (!response.success) {
            throw new Error(response.error || 'Password reset failed');
        }
    }
    /**
     * Confirm password reset with verification code and new password
     */
    async confirmResetPassword(username, confirmationCode, newPassword) {
        const response = await this.makeRequest('/auth/confirm-reset-password', {
            username,
            confirmationCode,
            newPassword
        });
        if (!response.success) {
            throw new Error(response.error || 'Password reset confirmation failed');
        }
    }
    /**
     * Refresh access token using refresh token
     */
    async refreshToken(refreshToken) {
        const response = await this.makeRequest('/auth/refresh-token', {
            refreshToken
        });
        if (!response.success) {
            throw new Error(response.error || 'Token refresh failed');
        }
        return response.data;
    }
    /**
     * Revoke all user sessions (force logout from all devices)
     */
    async revokeUserSessions(uid) {
        const response = await this.makeRequest('/auth/revoke-sessions', {
            uid
        });
        if (!response.success) {
            throw new Error(response.error || 'Failed to revoke user sessions');
        }
    }
    /**
     * Make authenticated request to backend
     */
    async makeRequest(endpoint, data) {
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        }
        catch (error) {
            console.error('Auth service request failed:', error);
            throw new Error('Network error occurred');
        }
    }
}
// Export singleton instance
export const secureAuthService = new SecureAuthService();
